import { useEffect, type ReactNode } from 'react';
import { BRAND } from '@/brand';
import { fallBackToBrandMark, useTenantLogo } from '@/lib/useTenantLogo';

/**
 * The frame for the screens outside the shell: sign-in and first-run setup
 * (cascade plan 3.5).
 *
 * d2d's look (docs/design/d2d/d2d.md): a white card on the page, over the soft
 * Mist Violet section tint that fades in from the top; gradients sit behind a
 * surface, never behind text. Above the card, the Logo Lockup: the mark, and
 * the wordmark in the display face at 500 with line-height 1. The mark is the
 * tenant's own logo when it has uploaded one (Settings > Branding).
 */
export function AuthLayout({
  subtitle,
  children,
  footer,
  tenantSlug = null,
}: {
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** The tenant whose logo to show, when known. */
  tenantSlug?: string | null;
}) {
  const logo = useTenantLogo(tenantSlug);
  // The shell titles the tab by page; signed out, the last page's title
  // would stay ("Settings · …").
  useEffect(() => {
    document.title = BRAND.name;
  }, []);
  return (
    <div className="relative grid min-h-[100dvh] place-items-center overflow-hidden bg-[var(--d2d-bg-primary)] px-4 py-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-[radial-gradient(ellipse_60%_70%_at_50%_0%,var(--brand-glow),transparent_70%)]"
      />
      <div className="relative flex w-full max-w-[420px] flex-col gap-6">
        <header className="flex flex-col items-center gap-3 text-center">
          {logo ? (
            <img src={logo} alt="" aria-hidden="true" className="h-12 w-auto max-w-[240px] object-contain" onError={fallBackToBrandMark} />
          ) : (
            <div aria-hidden="true" className="h-12" />
          )}
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-heading-sm font-medium leading-none text-[var(--d2d-text-primary)]">{BRAND.name}</h1>
            {subtitle ? <p className="text-dense text-[var(--d2d-text-muted)]">{subtitle}</p> : null}
          </div>
        </header>
        {children}
        {footer ? <p className="text-center text-micro text-[var(--d2d-text-muted)]">{footer}</p> : null}
      </div>
    </div>
  );
}
